import type {
  APIPermissionProfilePresetDuplicateRequestType,
  APIPermissionProfilePresetRequestType,
  APIPermissionProfilePresetResponseType,
  APIPermissionProfilePresetUsageResponseType,
  CompatibleRole,
  PresetContext,
  PresetStatus,
} from "@/core/data_sources/models/APIPermissionProfilePreset";
import type { BasicQuery } from "@/core/data_sources/services";
import { del, get, patch, post, put } from "@/core/utils/request";

export type PermissionProfilePresetsQuery = BasicQuery &
  Partial<{
    status: PresetStatus;
    compatible_role: CompatibleRole;
    context: PresetContext;
    search: string;
  }>;

const BASE = "/super-admin/permission-profile-preset/";

export function listPermissionProfilePresets<
  Q extends PermissionProfilePresetsQuery,
>(query: Q) {
  return get<APIPermissionProfilePresetResponseType[]>(BASE, { params: query });
}

export function getPermissionProfilePreset(id: string | number) {
  return get<APIPermissionProfilePresetResponseType>(`${BASE}${id}/`);
}

export function createPermissionProfilePreset(
  data: APIPermissionProfilePresetRequestType,
) {
  return post<
    APIPermissionProfilePresetRequestType,
    APIPermissionProfilePresetResponseType
  >(BASE, data);
}

export function patchPermissionProfilePreset(
  id: string | number,
  data: APIPermissionProfilePresetRequestType,
) {
  return patch<
    APIPermissionProfilePresetRequestType,
    APIPermissionProfilePresetResponseType
  >(`${BASE}${id}/`, data);
}

export function putPermissionProfilePreset(
  id: string | number,
  data: APIPermissionProfilePresetRequestType,
) {
  return put<
    APIPermissionProfilePresetRequestType,
    APIPermissionProfilePresetResponseType
  >(`${BASE}${id}/`, data);
}

export function deletePermissionProfilePreset(id: string | number) {
  return del<APIPermissionProfilePresetResponseType>(`${BASE}${id}/`);
}

export function duplicatePermissionProfilePreset(
  id: string | number,
  data: APIPermissionProfilePresetDuplicateRequestType,
) {
  return post<
    APIPermissionProfilePresetDuplicateRequestType,
    APIPermissionProfilePresetResponseType
  >(`${BASE}${id}/duplicate/`, data);
}

export function disablePermissionProfilePreset(id: string | number) {
  return post<undefined, APIPermissionProfilePresetResponseType>(
    `${BASE}${id}/disable/`,
  );
}

export function enablePermissionProfilePreset(id: string | number) {
  return post<undefined, APIPermissionProfilePresetResponseType>(
    `${BASE}${id}/enable/`,
  );
}

export function getPermissionProfilePresetUsage(id: string | number) {
  return get<APIPermissionProfilePresetUsageResponseType>(
    `${BASE}${id}/usage/`,
  );
}

export function getPermissionProfilePresetByQuery<
  Q extends { id: string | number } & BasicQuery,
>(query: Q) {
  return getPermissionProfilePreset(query.id);
}

export function getPermissionProfilePresetUsageByQuery<
  Q extends { id: string | number } & BasicQuery,
>(query: Q) {
  return getPermissionProfilePresetUsage(query.id);
}
